/**
 * The Complete step's screen (manual-booking-redesign D9; shared-client-screens D3, web-account-pages
 * D11), drawn for the Mini App's `/booking-hub/booking/:id/complete` and the studio website's
 * `/account/bookings/:id/complete` alike. Props only: the app's CompleteBookingPage and the website's
 * Complete booking page are its data parts over `useCompleteStep`.
 *
 * Visual authority: docs/mockups/client-dashboard-redesign/manual-booking-complete.html frames 3 and
 * 4 (the phone, both sides) and docs/mockups/telify-web/complete-booking.html (the computer). The
 * surface is the shipped FormRequestPage (pinned head, intro, FormRenderer clientBooking, inline CTA)
 * with the kicker "Complete booking", the service as the name, the step line where the type line
 * sits, the flow's reserved-until pill as "Finish by {deadline}" and the flow's CTA labels.
 *
 * - `CompleteStepScreen`: the whole screen, as the app draws it. Owned here: the mount scroll reset,
 *   the viewport variables, the native keyboard scroll held off, the keyboard-aware scroll of the
 *   fields and the scroll lock. Not here (D2): Telegram's safe area and the `data-client-theme`
 *   effect stay in the app's data part.
 * - `CompleteStepColumn`: the same step in a column of the computer website's page, under its
 *   header and breadcrumb, with none of the screen's rails.
 */
import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import { FormRenderer } from '@/components/forms/FormRenderer';
import { PrivacyConsentRowView } from '@/components/forms/PrivacyConsentRowView';
import { FlowBackButton } from '@/components/booking/FlowBackButton';
import { useClientLock } from '@/hooks/useClientLock';
import { useKeyboardAwareScroll } from '@/hooks/useKeyboardAwareScroll';
import { usePreventNativeKeyboardScroll } from '@/hooks/usePreventNativeKeyboardScroll';
import { useAppViewportCssVars } from '@/hooks/useVisualViewportCssVars';
import type { WaitingOutstanding } from '@/types/dashboard';
import type { FieldError, FormDefinition, FormResponseInput } from '@/types/forms';
import { formatFinishBy } from '@/utils/waitingBooking';
import { ctaLabel } from '../DetailsPage/formHeader';
import { completeStepLines, STEP_FAILURE } from './completeStepParts';
import type { CompleteStep, CompleteStepFlow, CompleteStepState } from './useCompleteStep';
import styles from '../FormRequestPage/FormRequestPage.module.css';
import ownStyles from './CompleteBookingPage.module.css';

export interface CompleteStepScreenProps {
  status: CompleteStepState;
  step: CompleteStep | null;
  completeBy: string | null;
  serverErrors: FieldError[];
  privacy: CompleteStepFlow['privacy'];
  /** The offering's name; the form's title until the booking is read. */
  serviceName: string | null;
  /** What the booking still owes: whether the payment follows the last form. */
  owed: Pick<WaitingOutstanding, 'forms' | 'payment' | 'approval'> | null | undefined;
  onSubmit: (responses: FormResponseInput[]) => void;
  onRetry: () => void;
  /** Back to the booking. */
  onBack: () => void;
  /** Opens the privacy policy (the app: outside Telegram); left out, the link opens its own new tab. */
  onOpenPolicy?: (url: string) => void;
}

/** The step's intro and its form with the CTA. */
function StepBody({ step, completeBy, serverErrors, privacy, owed, status, onSubmit, onOpenPolicy }: CompleteStepScreenProps & { step: CompleteStep }) {
  const form: FormDefinition = step.form;
  const { typeLine, isLastForm, hasPaymentAhead } = completeStepLines(step, owed);
  return (
    <div className={styles.content}>
      <div className={styles.intro}>
        <div className={styles.req}>{typeLine}</div>
        {form.description && <div className={styles.fdesc}>{form.description}</div>}
        {completeBy && (
          <div className={ownStyles.finishBy}>
            <ClockIcon />
            Finish by {formatFinishBy(completeBy)}
          </div>
        )}
      </div>

      <FormRenderer
        key={form.formId}
        form={form}
        onSubmit={onSubmit}
        onSave={noSave}
        isSubmitting={status === 'submitting'}
        serverErrors={serverErrors}
        disableAutoSave
        appearance="clientBooking"
        renderActions={({ formReady, isSubmitting }) => (
          <>
            {privacy.required && (
              <PrivacyConsentRowView checked={privacy.ticked} onChange={privacy.onChange} showError={privacy.error} onOpenPolicy={onOpenPolicy} />
            )}
            <div className={styles.formbar}>
              <button type="submit" className={`${styles.btnPrimary} ${isSubmitting ? styles.busy : ''}`} disabled={isSubmitting}>
                {isSubmitting && <span className={styles.btnSpin} aria-hidden="true" />}
                {ctaLabel({ formType: form.formType, isLastForm, hasPaymentAhead, formReady })}
                {!isSubmitting && <ArrowRightIcon />}
              </button>
            </div>
          </>
        )}
      />
    </div>
  );
}

// Auto-save is off: each form is one submit; the slot is required by FormRenderer.
const noSave = () => {};

/** Loading, or the step that could not load with Try again and Back to booking. */
function StepState({ status, onRetry, onBack }: Pick<CompleteStepScreenProps, 'status' | 'onRetry' | 'onBack'>) {
  return status === 'error' ? (
    <div className={styles.term}>
      <div className={styles.termCard}>
        <div className={styles.termOrb}><AlertIcon /></div>
        <h2>{STEP_FAILURE.title}</h2>
        <p>{STEP_FAILURE.message}</p>
        <div className={styles.termActions}>
          <button type="button" className={styles.termBtn} onClick={onRetry}>
            Try again
          </button>
          <button type="button" className={`${styles.termBtn} ${styles.termBtnSecondary}`} onClick={onBack}>
            Back to booking
          </button>
        </div>
      </div>
    </div>
  ) : (
    <div className={styles.loading}>
      <div className={styles.spinner} />
    </div>
  );
}

/** Each new step opens at its top. */
function useTopOnStep(ref: RefObject<HTMLElement | null>, step: CompleteStep | null) {
  useEffect(() => {
    if (step) ref.current?.scrollTo?.(0, 0);
  }, [ref, step]);
}

export function CompleteStepScreen(props: CompleteStepScreenProps) {
  const { status, step, serviceName, onBack } = props;
  // Scroll to top on mount (WKWebView reset, see feedback_wkwebview_keyboard_scroll_fix).
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  useAppViewportCssVars();
  usePreventNativeKeyboardScroll();
  const scrollRef = useRef<HTMLDivElement>(null);
  useKeyboardAwareScroll(scrollRef, { bottomPadding: 24 });
  useClientLock();
  useTopOnStep(scrollRef, step);

  if (status === 'loading' || status === 'error' || !step) {
    return (
      <div className={styles.page}>
        <div className={styles.atmosphere} aria-hidden="true" />
        <StepState {...props} />
      </div>
    );
  }

  return (
    <div className={styles.flow}>
      <div className={styles.scroll} ref={scrollRef}>
        <div className={styles.head}>
          <FlowBackButton className={styles.headBack} onClick={onBack} />
          <div className={styles.headText}>
            <div className={styles.kicker}>Complete booking</div>
            <h1 className={styles.fname}>{serviceName ?? step.form.title}</h1>
          </div>
        </div>
        <StepBody {...props} step={step} />
      </div>
    </div>
  );
}

export function CompleteStepColumn(props: CompleteStepScreenProps) {
  const { status, step, serviceName } = props;
  const topRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (step) topRef.current?.scrollIntoView?.({ block: 'start' });
  }, [step]);

  return (
    <div className={ownStyles.column} ref={topRef}>
      {status === 'loading' || status === 'error' || !step ? (
        <div className={ownStyles.columnState}>
          <StepState {...props} />
        </div>
      ) : (
        <>
          <div className={ownStyles.columnHead}>
            <div className={styles.kicker}>Complete booking</div>
            <h1 className={styles.fname}>{serviceName ?? step.form.title}</h1>
          </div>
          <StepBody {...props} step={step} />
        </>
      )}
    </div>
  );
}

// ============================================
// Icons
// ============================================

/** The flow's reserved-until clock (DetailsPage). */
function ClockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function ArrowRightIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}
